/* eslint-disable class-methods-use-this */
import {
  ClientResponse,
  ConversationEventType,
  ConversationMessage,
  ConversationMessageStatus,
  SendConversationMessageParams,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessagePart,
  ConversationsApiDataMessagePartText,
  ConversationsApiSendMessageEndpointRequest,
} from '../generated/types';
import {ErrorResponse, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

type SendConversationMessageResponse =
  | {type: 'success'; data: ConversationsApiDataConversation}
  | ErrorResponse;

type SendConversationMessageHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
  ) => Promise<SendConversationMessageResponse>;
  emitConversationEvent: (event: ConversationEventType) => void;
  reportWarning?: ReportWarning;
};

type ConversationsApiTextPartWithoutId = Omit<
  ConversationsApiDataMessagePartText,
  'id'
>;

export class SendConversationMessageHandler {
  private deps: SendConversationMessageHandlerDeps;

  constructor(deps: SendConversationMessageHandlerDeps) {
    this.deps = deps;
  }

  call = async (
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> => {
    if (!params.conversationId) {
      return {code: 400, error: 'Missing conversationId'};
    }
    if (!params.message.participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const payload = this.buildApiMessage(params.message);
    if (!payload) {
      return {
        code: 400,
        error: `Unsupported message type: ${params.message.type}`,
      };
    }

    this.emitOptimisticMessage(params);

    let response;
    try {
      response = await this.deps.sendMessage(params.conversationId, payload);
    } catch (e) {
      this.emitMessageStatus(params, ConversationMessageStatus.Error);
      throw e;
    }

    if (response.type === 'error') {
      this.emitMessageStatus(params, ConversationMessageStatus.Error);
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    this.emitMessageStatus(params, ConversationMessageStatus.Sent);
    const {conversation, newMessages} = FrameworkAdapter.diffConversation({
      previousMessageIds: [params.message.id],
      apiConversation: response.data,
      requestContext: {},
      user: params.message.participant,
      minimumTimestamp: params.message.timestamp,
    });

    this.emitInputModeContextValues(
      params.conversationId,
      conversation.context.enabledInputModes,
    );

    newMessages.forEach((message) => {
      this.deps.emitConversationEvent({
        name: 'conversation-message-added',
        conversationId: params.conversationId,
        participantId: message.participant.id,
        messageId: message.id,
        nonce: message.nonce,
        message,
      });
    });

    return {code: 200};
  };

  private emitOptimisticMessage(params: SendConversationMessageParams): void {
    this.deps.emitConversationEvent({
      name: 'conversation-message-added',
      conversationId: params.conversationId,
      participantId: params.message.participant.id,
      messageId: params.message.id,
      nonce: params.message.nonce,
      message: params.message,
    });
  }

  private emitMessageStatus(
    params: SendConversationMessageParams,
    status: ConversationMessageStatus,
  ): void {
    const message: ConversationMessage = {
      ...params.message,
      status,
    };
    this.deps.emitConversationEvent({
      name: 'conversation-message-updated',
      conversationId: params.conversationId,
      participantId: params.message.participant.id,
      messageId: params.message.id,
      nonce: params.message.nonce,
      message,
    });
  }

  private buildApiMessage(
    message: ConversationMessage,
  ): ConversationsApiSendMessageEndpointRequest | null {
    const parts = this.buildApiParts(message);
    if (!parts) {
      this.deps.reportWarning?.('Unsupported Conversations API message type', {
        tags: {message_type: message.type},
      });
      return null;
    }

    return {
      id: message.id,
      participant_id: message.participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        message.participant.role,
      ),
      parts,
      page_context: this.extractPageContext(message.data),
    };
  }

  private buildApiParts(
    message: ConversationMessage,
  ): ConversationsApiDataMessagePart[] | null {
    if (message.type === 'text-conversation-message') {
      return this.assignPartIds([
        {
          type: 'text',
          content: {text: message.text},
        },
      ]);
    }

    if (message.type === 'attachment-conversation-message') {
      return this.assignPartIds([
        this.buildAttachmentPart({
          text: message.attachment.name,
        }),
      ]);
    }

    if (message.type === 'text-with-attachments-conversation-message') {
      const textPart: ConversationsApiTextPartWithoutId = {
        type: 'text',
        content: {text: message.text},
      };
      return this.assignPartIds([
        textPart,
        ...message.attachments.map((attachment) =>
          this.buildAttachmentPart({
            text: attachment.name,
          }),
        ),
      ]);
    }

    return null;
  }

  private buildAttachmentPart({
    text,
  }: {
    text: string;
  }): ConversationsApiTextPartWithoutId {
    return {
      type: 'text',
      content: {text},
    };
  }

  private assignPartIds(
    parts: ConversationsApiTextPartWithoutId[],
  ): ConversationsApiDataMessagePart[] {
    return parts.map((part, index) => ({
      id: index + 1,
      ...part,
    }));
  }

  private extractPageContext(
    data?: Record<string, unknown>,
  ): Record<string, string> | undefined {
    const candidate =
      (data?.page_context as Record<string, unknown> | undefined) ??
      (data?.pageContext as Record<string, unknown> | undefined);
    if (!candidate || typeof candidate !== 'object') {
      return undefined;
    }

    const pageContext = Object.fromEntries(
      Object.entries(candidate).map(([key, value]) => {
        const normalized =
          value &&
          typeof value === 'object' &&
          'value' in (value as Record<string, unknown>)
            ? (value as {value: unknown}).value
            : value;
        return [key, String(normalized)];
      }),
    );
    return Object.keys(pageContext).length > 0 ? pageContext : undefined;
  }

  private emitInputModeContextValues(
    conversationId: string,
    enabledInputModes: unknown,
  ): void {
    if (
      !Array.isArray(enabledInputModes) ||
      !enabledInputModes.every((mode) => typeof mode === 'string')
    ) {
      return;
    }

    this.deps.emitConversationEvent({
      name: 'conversation-set-context-values',
      conversationId,
      values: {enabledInputModes},
    });
  }
}
